import { useState } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import AutoLayout from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayout";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "jadro-pico-app/Components/UI/Layouts/AutoLayout/AutoLayoutEnums";
import FontText from "jadro-pico-app/Components/UI/FontText/FontText";
import { FontSizeEnum, FontTypeEnum } from "jadro-pico-app/Components/UI/FontText/FontTextEnums";
import { ThemeColorEnum } from "jadro-pico-app/Components/UI/Themes/ThemeEnums";
import SectionCard from "jadro-pico-app/Components/UI/SectionCard/SectionCard";
import { SectionCardVariantEnum } from "jadro-pico-app/Components/UI/SectionCard/SectionCardVariantEnum";
import { FilledButton, TextButton } from "jadro-pico-app/Components/UI/Button";
import TextField from "jadro-pico-app/Components/UI/TextField/TextField";
import FormField from "jadro-pico-app/Components/UI/FormField/FormField";
import Skeleton from "jadro-pico-app/Components/UI/Skeleton/Skeleton";
import { SkeletonShapeEnum } from "jadro-pico-app/Components/UI/Skeleton/SkeletonEnums";
import { useApiQuery } from "jadro-pico-app/Api/useApiQuery";
import { useDeferrableMutation } from "jadro-pico-app/Api/useDeferrableMutation";
import { showSnackbar } from "jadro-pico-app/Components/UI/Snackbar/showSnackbar";
import { useResource } from "jadro-pico-app/Resources/useResource";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths";
import type { ApiResponse } from "jadro-pico/dtos/common/ApiResponse";
import type { UserProfileDto } from "jadro-pico/dtos/chlast/UserProfileDto";
import { WEIGHT_MIN_KG, WEIGHT_MAX_KG } from "jadro-pico/validators/chlast/UpdateUserProfileSchema";
import { UserProfileResources } from "../../Resources/UserProfileResources";
import { CommonResources } from "../../Resources/CommonResources";
import { QueryKeys } from "../../queryKeys";
import BackHeaderPage from "jadro-pico-app/Components/UI/Layouts/PageLayout/BackHeaderPage";

const PROFILE_URL = `${import.meta.env.VITE_PICO_API_URL}${ApiPaths.chlast.profile.prefix}${ApiPaths.chlast.profile.me}`;

interface UserProfileSectionProps {
  onCollapse: () => void;
}

function UserProfileSection({ onCollapse }: UserProfileSectionProps) {
  const t = useResource(UserProfileResources);
  const common = useResource(CommonResources);
  const [draftOverride, setDraftOverride] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useApiQuery<ApiResponse<UserProfileDto>>({
    url: PROFILE_URL,
    queryKey: QueryKeys.userProfile,
    retry: false,
  });

  const mutation = useDeferrableMutation<ApiResponse<unknown>, UserProfileDto>({
    url: PROFILE_URL,
    method: "PUT",
    operationLabel: t.operationLabel,
  });

  const profile: UserProfileDto = data?.data ?? { weight: null };
  const serverWeight = profile.weight != null ? String(profile.weight) : "";
  const draft = draftOverride ?? serverWeight;

  const save = async () => {
    const trimmed = draft.trim();
    let weight: number | null;
    if (trimmed === "") {
      weight = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < WEIGHT_MIN_KG || parsed > WEIGHT_MAX_KG) {
        setError(t.weightRangeError);
        return;
      }
      weight = parsed;
    }
    const payload: UserProfileDto = { weight };
    try {
      await mutation.mutateAsync(payload);
      void queryClient.invalidateQueries({ queryKey: QueryKeys.userProfile });
      showSnackbar(dispatch, t.saveSubmitted);
      onCollapse();
    } catch {
      setError(common.errorSave);
    }
  };

  const loadError = isError
    ? {
        headline: common.errorLoadHeadline,
        supporting: common.errorLoadSupporting,
        retry: { label: common.actionRetry, onClick: () => void refetch() },
      }
    : undefined;

  const formSkeleton = (
    <SectionCard variant={SectionCardVariantEnum.SURFACE}>
      <Skeleton shape={SkeletonShapeEnum.TEXT} width="40%" />
      <Skeleton shape={SkeletonShapeEnum.RECTANGLE} height={56} />
    </SectionCard>
  );

  return (
    <BackHeaderPage
      title={t.sectionTitle}
      backLabel={common.actionBack}
      onBack={onCollapse}
      isLoading={isLoading}
      error={loadError}
      loadingSkeleton={formSkeleton}
    >
      <SectionCard variant={SectionCardVariantEnum.SURFACE}>
        <FormField label={t.fieldWeight}>
          <TextField
            type="number"
            inputMode="decimal"
            step="0.1"
            min={WEIGHT_MIN_KG}
            max={WEIGHT_MAX_KG}
            value={draft}
            onChange={(e) => setDraftOverride(e.target.value)}
          />
        </FormField>
        {error && (
          <FontText
            text={error}
            $fontSize={FontSizeEnum.SMALL}
            $fontType={FontTypeEnum.BODY}
            $color={ThemeColorEnum.ERROR}
          />
        )}
        <AutoLayout
          $direction={AutoLayoutDirectionEnum.HORIZONTAL}
          $align={AutoLayoutAlignEnum.RIGHT}
          $horizontalResizing={AutoLayoutResizingEnum.FILL}
          $gap={8}
        >
          <TextButton label={common.actionCancel} onClick={onCollapse} />
          <FilledButton
            label={common.actionSave}
            onClick={() => void save()}
            disabled={mutation.isPending}
          />
        </AutoLayout>
      </SectionCard>
    </BackHeaderPage>
  );
}

export default UserProfileSection;
